import { useMemo } from 'react';

import { useAddIncentiveToCart } from '@fhs/loyalty/src/hooks/use-add-incentive-to-cart';
import { useLoyaltyLegacyStates } from '@fhs/loyalty/src/hooks/use-loyalty-legacy-states';
import { useRemoveOffer } from '@fhs/loyalty/src/hooks/use-remove-offer';
import { useRewardTierApplied } from '@fhs/loyalty/src/hooks/use-reward-tier-applied';
import { useRewardTiers } from '@fhs/loyalty/src/queries/loyalty.queries';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { ICartEntry } from '@rbi-ctg/menu';

import RewardInUse from './redeemed-reward';
import RewardAvailable from './reward-available';

interface IRewardPointsBarProps {
  item: ICartEntry;
}

export default function RewardPointsBar({ item }: IRewardPointsBarProps) {
  const { data } = useRewardTiers();
  const { rewardTierApplied } = useRewardTierApplied();
  const { loyaltyUser } = useLoyaltyLegacyStates();
  const { addIncentiveToCart } = useAddIncentiveToCart();
  const { removeOffer } = useRemoveOffer();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const incentiveSelections = useAppSelector(selectors.loyalty.selectSelectedOfferSelections);

  const eligibleRewardTier = useMemo(
    () => data?.find(tier => tier.eligibleItems.some(itemId => itemId === item._id)),
    [data, item._id]
  );

  const isRewardApplied = useMemo(
    () =>
      incentiveSelections.some(
        selection =>
          selection.incentiveId === rewardTierApplied?.id && selection.lineId === item.cartId
      ),
    [incentiveSelections, rewardTierApplied, item.cartId]
  );

  const displayRewardTier = useMemo(() => {
    const tier = rewardTierApplied || eligibleRewardTier;
    if (!tier) return null;
    return { id: tier.id, name: tier.name, points: tier.points };
  }, [rewardTierApplied, eligibleRewardTier]);

  const hasAppliedOffers = appliedOffers.length > 0;
  const hasSufficientBalance =
    loyaltyUser?.points != null &&
    displayRewardTier?.points != null &&
    displayRewardTier.points <= loyaltyUser.points;
  if (
    !displayRewardTier ||
    (hasAppliedOffers && !isRewardApplied) ||
    (!hasSufficientBalance && !isRewardApplied)
  ) {
    return null;
  }

  const onRemoveReward = () => removeOffer({ showDialog: false });

  const onAddReward = () => {
    addIncentiveToCart({
      offer: {
        incentiveType: 'REWARD',
        id: displayRewardTier.id,
        cmsId: displayRewardTier.id,
        name: displayRewardTier.name,
        description: '',
        requireGuideFlow: true,
        requireTierSelector: false,
        pricingStrategy: {
          pricingStrategy: 'DISCOUNT',
          type: 'percentage',
          value: 100,
        },
      },
      selections: [
        {
          incentiveId: displayRewardTier.id,
          key: 'appliedProduct',
          lineId: item.cartId,
          quantity: 1,
          rank: 0,
        },
      ],
    });
  };

  if (isRewardApplied) {
    return <RewardInUse displayRewardTier={displayRewardTier} onRemoveReward={onRemoveReward} />;
  }

  return <RewardAvailable displayRewardTier={displayRewardTier} onAddReward={onAddReward} />;
}
